import * as React from 'react';

import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Snackbar from '@mui/material/Snackbar';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { type User } from '@/auth';
import Log from '@/Logger';
import { FeatureContext } from '../../../FeatureContext';
import { getUrlQueryParams, isOAuthRedirectFromKintone } from '../../util/kintone/index';
import { useConfirmDialog } from '../../util/useConfirmDialog';
import UserFormDialog, { type InputField } from '../../util/useUserForm';
import { getOAuthStateAndDelete, linkServiceTokenGeneratedFromCode, registerToken } from './api';
import { kintoneErrorMessage } from './errorMessage';
import { getUserSettingContentId, ServiceName, tenantSettingId } from './settingContents';
import { getTenantSetting, getUserSetting } from './utils';

type Props = {
    user: User;
};

type UserSetting = {
    icon: React.JSX.Element;
    id: string;
    label: string;
    state: boolean;

    enablingMessage?: string | React.ReactNode;
    enabledMessage?: string;
    disablingMessage?: string;
    disabledMessage?: string;

    failedToEnabledMessage?: string;
    failedToDisabledMessage?: string;

    turnOn?: (id?: string) => Promise<void>;
    turnOff?: (id?: string) => Promise<void>;
    submit?: (data: Record<string, string>) => Promise<void>;
}

function icon(s: { id: string }) {
    //項目ごとにアイコンを設定する
    return (<SettingsOutlinedIcon />);
}

function LinkServiceSetting(props: Props) {
    const logger = Log.getLogger();

    const queryParams = getUrlQueryParams()
    const features = React.useContext(FeatureContext);

    const [userSetting, setUserSetting] = React.useState<UserSetting[]>([]);
    const [tenantSetting, setTenantSetting] = React.useState<UserSetting[]>([]);
    const [pending, setPending] = React.useState(true);
    const [ConfirmDialog, { openConfirmDialog, setConfirmDialogMessage }] = useConfirmDialog();
    const [formOpen, setFormOpen] = React.useState(false);
    const [inputFields, setInputField] = React.useState<InputField[]>([])
    const [formTexts, setFormTexts] = React.useState<{ title: string; contentText: string | React.ReactNode }>({ title: '', contentText: '', });
    const [submitServiceId, setSubmitServiceId] = React.useState('')
    const [snackBarOpen, setSnackBarOpen] = React.useState(false);
    const [snackBarText, setSnackBarText] = React.useState('');
    const [snackBarType, setSnackBarType] = React.useState<any>(undefined);

    const handleSnackBarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackBarOpen(false);
    };

    //初回描画、リフレッシュの描画をこの関数で行う
    async function refreshRender(isChangedOn?: boolean, isTenantSettingRetry?: boolean): Promise<any> {
        setPending(true)//ぐるぐるマークを入れて読み込み中なのを伝える

        try {
            //トグルがonになったとき(isChangedOn)はこの処理を行わない
            if (isOAuthRedirectFromKintone(queryParams) && !isChangedOn) {//kintoneでログイン後に遷移してきたかを判定(stateの検証はまだ行わない)
                logger.ui.info('LinkServiceSetting', 'refreshRender', 'Redirected from kintone.')

                //サーバ側のstateを取ってきて検証を行う。
                const sentState = await getOAuthStateAndDelete(ServiceName.kintone)
                //一致したらトークンのリクエストを行う
                if (queryParams['state'] === sentState.state) {
                    await requestNewKintoneToken()
                }
            }

            //管理者ユーザの場合のみテナント設定項目を出す()
            if (features.registerKintoneSetting()) {
                const tenantSetting = await getTenantSetting(isChangedOn ?? false, isTenantSettingRetry ?? false)//引数として受け取らない場合はリトライを行わない
                if (tenantSetting) {
                    const s = tenantSetting.map(tenantSetting => ({
                        icon: icon(tenantSetting),
                        id: tenantSetting.id,
                        label: tenantSetting.name,
                        state: tenantSetting.state === 'ENABLED',
                        enablingMessage: tenantSetting.enablingMessage,
                        enabledMessage: tenantSetting.enabledMessage,
                        disablingMessage: tenantSetting.disablingMessage,
                        disabledMessage: tenantSetting.disabledMessage,

                        failedToEnabledMessage: tenantSetting.failedToEnabledMessage,
                        failedToDisabledMessage: tenantSetting.failedToDisabledMessage,

                        turnOn: tenantSetting?.turnOnFunction,
                        turnOff: tenantSetting?.turnOffFunction,
                        submit: tenantSetting?.submitFunction
                    }));
                    setTenantSetting(s);
                }
            }

            //リダイレクトの場合はリトライ設定を有効にし、DB反映を待てるようにする
            const userSetting = await getUserSetting(isOAuthRedirectFromKintone(queryParams));
            if (userSetting) {
                const s = userSetting.map(userSetting => ({
                    icon: icon(userSetting),
                    id: userSetting.id,
                    label: userSetting.name,
                    state: userSetting.state === 'ENABLED',
                    enablingMessage: userSetting.enablingMessage,
                    failedToEnabledMessage: userSetting.failedToEnabledMessage,
                    failedToDisabledMessage: userSetting.failedToDisabledMessage,
                    enabledMessage: userSetting.enabledMessage,
                    disablingMessage: userSetting.disablingMessage,
                    disabledMessage: userSetting.disabledMessage,

                    turnOn: userSetting?.turnOnFunction,
                    turnOff: userSetting?.turnOffFunction,
                    submit: userSetting?.submitFunction
                }));
                setUserSetting(s);
            }

            setPending(false);
        } catch (e) {
            setPending(false)
        }
    }

    // 画面を開いたときの初回実行
    React.useEffect(() => {
        refreshRender()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //kintoneから認可後に遷移してきた場合にトークンをリクエストする
    //渡されてきた認可コードを使う
    async function requestNewKintoneToken(): Promise<boolean> {
        try {
            const code = queryParams['code'];

            const tokens = await linkServiceTokenGeneratedFromCode(ServiceName.kintone, code);
            await registerToken(ServiceName.kintone, tokens.accessToken, tokens.refreshToken);

            const enabledMessage = getUserSettingContentId(ServiceName.kintone)?.enabledMessage;
            if (enabledMessage) {
                setSnackBarType('success');
                setSnackBarText(enabledMessage);
                setSnackBarOpen(true);
            }

            return true;
        } catch (error) {
            //通常の手順ならエラーになることはない。
            //ネットワークの関係で遷移に時間がかかり認可コードが期限切れになる、不正なコードが入力される、など。
            logger.ui.error('LinkServiceSetting', 'requestNewToken', 'Failed to login kintone');
            setSnackBarType('error');
            setSnackBarText(kintoneErrorMessage.failedToIssueToken);
            setSnackBarOpen(true);

            return false;
        }
    }

    /**
     * スイッチが押されたとき(オンオフされるとき)に呼び出される関数
     * 
     * @param {string} id - オンオフされた項目のid
     * @param {boolean} newState - 新しい状態: true: on, false: off
     * @param {boolean} isTenantChange - テナントの設定かユーザ設定かのフラグ
     * 
     * @description
     * 指定された設定の ON/OFF を切り替える。
     * `isTenantChange` が `true` の場合は `tenantSetting` から設定を取得し、
     * `false` の場合は `userSetting` から取得する。
     * 
     * - `newState` が `true`（ON）の場合は `handleToggleEnabling` を実行
     * - `newState` が `false`（OFF）の場合は `handleToggleDisabling` を実行
     */
    const handleToggle = (id: string, newState: boolean, isTenantChange: boolean) => async () => {
        logger.ui.info('LinkServiceSetting', 'handleToggle', `${id} is handled. new state: ${newState}`);

        let settingMsg: UserSetting | undefined;
        if (isTenantChange) {
            settingMsg = tenantSetting.find(f => f.id === id);
        } else {
            settingMsg = userSetting.find(f => f.id === id);
        }

        if (newState) {//ONにされたとき
            await handleToggleEnabling(id, settingMsg, isTenantChange)
        } else {//OFFにしたとき
            await handleToggleDisabling(id, settingMsg, isTenantChange)
        }
    };

    //トグルがオンにされたときに呼び出される
    async function handleToggleEnabling(id: string, settingMsg: UserSetting | undefined, isTenantChange: boolean) {

        if (settingMsg?.enablingMessage) {
            const turnOnFunc = settingMsg.turnOn;

            //turnOnFuncが設定されていない場合はhandleToggleEnablingWithoutFuncが呼ばれる
            if (turnOnFunc === undefined) {
                await handleToggleEnablingWithoutFunc(id)
                return;
            }

            //確認ダイアログを表示し、はいの場合のみ処理が動く
            setConfirmDialogMessage([settingMsg.enablingMessage as string], true);
            const buttonEvent = await openConfirmDialog();
            if (buttonEvent !== 'confirm') {
                return;
            }

            try {
                if (turnOnFunc) {
                    setPending(true)
                    await turnOnFunc()
                    logger.ui.info('LinkServiceSetting', 'handleToggleEnabling', `Succeeded to enabled ${id}`)
                }
            } catch (err) {
                logger.ui.error('LinkServiceSetting', 'handleToggleEnabling', `Failed to enabled ${id}`);

                setSnackBarType('error');
                setSnackBarText(settingMsg.failedToEnabledMessage as string);
                setSnackBarOpen(true);
            }

            await refreshRender(true, isTenantChange)
            return;
        }
    }

    //トグルがオフにされたときに動く
    async function handleToggleDisabling(id: string, settingMsg: UserSetting | undefined, isTenantChange: boolean) {

        if (settingMsg?.disablingMessage) {
            const turnOffFunc = settingMsg.turnOff;

            //turnOffFuncが設定されていない場合はhandleToggleDisablingWithoutFuncが呼ばれる
            if (turnOffFunc === undefined) {
                await handleToggleDisablingWithoutFunc(id);
                return;
            }

            //確認ダイアログを表示し、はいの場合のみ処理が動く
            setConfirmDialogMessage([settingMsg.disablingMessage as string], true);
            const buttonEvent = await openConfirmDialog();
            if (buttonEvent !== 'confirm') {
                return;
            }

            try {
                if (turnOffFunc) {
                    await turnOffFunc()
                    logger.ui.info('LinkServiceSetting', 'handleToggleDisabling', `Succeeded to disabled ${id}`)
                }

            } catch (err) {
                logger.ui.error('LinkServiceSetting', 'handleToggleDisabling', `Failed to disabled ${id}`);

                setSnackBarType('error');
                setSnackBarText(settingMsg.failedToDisabledMessage as string);
                setSnackBarOpen(true);
            }
            await refreshRender(false, isTenantChange)
            return;
        }
    }

    //turnOnFuncがsettingContentで設定されていない場合はこれが呼ばれる
    //settingContent内で書くには複雑な場合はここで定義を行う(disablingも同様)
    async function handleToggleEnablingWithoutFunc(id?: string) {
        try {
            switch (id) {
                case tenantSettingId.kintone: //kintoneのテナント設定の場合はフォームを出してユーザに入力させる
                    logger.ui.info('LinkServiceSetting', 'handleToggleEnabledWithoutFunc', `${id} is enabled`);

                    const msg = tenantSetting.find(f => f.id === id);
                    if (msg && msg.enablingMessage) {
                        setFormTexts({
                            title: '',
                            contentText: msg.enablingMessage
                        })

                        setInputField([
                            { label: 'クライアントID', name: 'clientId', type: 'text', required: true, isAlphanumeric: true },
                            { label: 'クライアントシークレット', name: 'secretId', type: 'text', required: true, isAlphanumeric: true },
                            { label: 'トークンエンドポイント', name: 'endpointUrl', type: 'url', required: true },
                        ])
                        setSubmitServiceId(id)
                        handleOpenForm()
                    }
                    return;
                default:
                    logger.ui.error('LinkServiceSettings', 'handleToggleEnabledWithoutFunc', `Failed to turn on: Undefined key: ${id}`)
            }

        } catch (e) {

            logger.ui.error('LinkServiceSetting', 'handleToggleDisabledWithoutFunc', `Failed to turn on: unexpected error: ${e instanceof Error ? e.message : String(e)}`)
            return;
        }
    }

    //turnOffFuncがsettingContentで設定されていない場合はこれが呼ばれる
    async function handleToggleDisablingWithoutFunc(id?: string) {
        try {
            switch (id) {
                default:
                    logger.ui.error('LinkServiceSettings', 'handleToggleDisabledWithoutFunc', `Failed to turn off: Undefined key: ${id}`)
            }
        } catch (e) {
            logger.ui.error('LinkServiceSetting', 'handleToggleDisabledWithoutFunc', `Failed to turn on: unexpected error: ${e instanceof Error ? e.message : String(e)}`)
            return;
        }

    }

    const handleOpenForm = () => {
        setFormOpen(true); // 他のボタンでダイアログを開く
    };

    const handleCloseForm = (action: 'submit' | 'cancel', data?: Record<string, string>) => {
        if (action === 'submit' && data) {
            setFormOpen(false);//　処理を待たずに先にダイアログを閉じる
            setPending(true)

            try {
                const submitFunc = getContentById(tenantSetting, submitServiceId, 'submit')
                if (typeof submitFunc === 'function') {
                    submitFunc(data)
                }
                refreshRender(true, true)

            } catch (err) {
                logger.ui.error('LinkServiceSetting', 'handleCloseForm', 'Failed to submit Form');
            }

        }
        setFormOpen(false);
    };

    return (
        <>
            <List
                sx={{ width: '100%', bgcolor: 'background.paper' }}
                subheader={<ListSubheader
                    sx={{ fontSize: '1.5rem', fontWeight: 'bold', mt: 2 }} // フォントサイズを調整
                >
                    サービス連携設定
                </ListSubheader>}
            >
                <Box component='section' marginLeft={3} marginBottom={1}>
                    <Typography color='text.secondary' variant='body2'>
                        外部サービスとの連携設定を変更します
                    </Typography>

                </Box>
                <Divider />

                {
                    pending ? <LinearProgress /> :
                        (tenantSetting == null && userSetting == null) || (tenantSetting.length === 0 && userSetting.length === 0) ? <Typography margin={1}>設定項目なし</Typography> :
                            tenantSetting.map((item) => (
                                features.registerKintoneSetting() ? (
                                    <ListItem key={item.id} divider={true}>
                                        <ListItemIcon sx={{ color: 'inherit', marginLeft: '25px' }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText id={'switch-list-label-' + item.id} primary={item.label} />
                                        <Switch
                                            edge='start'
                                            onChange={handleToggle(item.id, !item.state, true)}
                                            checked={item.state}
                                        />
                                    </ListItem>
                                ) : null
                            ))
                }
                {
                    pending ? <></> :
                        userSetting.map((item) => (
                            <ListItem
                                key={item.id}
                                divider={true}
                            >
                                <ListItemIcon sx={{ color: 'inherit', marginLeft: '25px' }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText id={'switch-list-label-' + item.id} primary={item.label} />
                                <Switch
                                    edge='start'
                                    onChange={handleToggle(item.id, !item.state, false)}
                                    checked={item.state}
                                />
                            </ListItem>
                        ))
                }

            </List>
            <ConfirmDialog key={'settings'} title='設定を変更しますか？' />
            <UserFormDialog
                open={formOpen}
                onClose={handleCloseForm}
                inputFields={inputFields}
                title={formTexts.title}
                contentText={formTexts.contentText}
            />
            <Snackbar open={snackBarOpen} onClose={handleSnackBarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleSnackBarClose} severity={snackBarType} sx={{ width: '100%' }} >
                    {snackBarText}
                </Alert>
            </Snackbar>
        </>
    );
};
export default LinkServiceSetting;

//特定のキーの特定のプロパティを取り出す
function getContentById<K extends keyof UserSetting>(settings: UserSetting[], id: string, messageType: K): UserSetting[K] | undefined {
    const setting = settings.find(s => s.id === id);
    if (!setting) {
        return undefined;
    }

    return setting[messageType];
};